import 'regenerator-runtime/runtime';

import type {
  DataRefId,
  MiddlewareStates,
  NetworkRequestor,
  OverreactRequestShape,
  OverreactSpec,
  RequestExecution,
  RequestHeaders,
  RequestSink,
  RequestVariables,
  RequestVerb,
} from '../public-types';
import type { Schema } from '../schema';
import type { Store } from '../store';
import {
  requestWithMiddleware,
  WrappedRequestor,
} from '../middleware';
import type { MiddlewareCollection } from '../middleware/request-with-middleware';

interface RequestSubscriber {
  notify(_requests: OverreactRequestShape[]): void;
}

interface RequestAdditionalParams {
  componentName?: string;
}

export class Environment<TResponse = unknown> {
  dataFetcherSubscriber: RequestSubscriber[];
  dataRefIdPool: Record<string, string>;
  middlewares: MiddlewareCollection<TResponse>;
  networkRequestor: NetworkRequestor<TResponse>;
  requestQueue: OverreactRequestShape[];
  schema: Schema;
  store: Store;
  tag?: string;

  constructor(
    networkRequestor: NetworkRequestor<TResponse>,
    schema: Schema,
    store: Store,
    middlewares: MiddlewareCollection<TResponse> = [],
    tag?: string,
  ) {
    this.networkRequestor = networkRequestor;
    this.schema = schema;
    this.requestQueue = [];
    this.dataFetcherSubscriber = [];
    this.store = store;
    this.middlewares = middlewares;
    this.tag = tag;
    this.dataRefIdPool = {};

    this.notifyObservers = this.notifyObservers.bind(this);
    this.pushRequest = this.pushRequest.bind(this);
    this.removeRequest = this.removeRequest.bind(this);
    this.requestCount = this.requestCount.bind(this);
    this.subscribe = this.subscribe.bind(this);
    this.unsubscribe = this.unsubscribe.bind(this);
    this.getSchema = this.getSchema.bind(this);
    this.getRequestor = this.getRequestor.bind(this);
  }

  getRequestor<TVariables extends object = RequestVariables>(
    id: DataRefId,
    spec: OverreactSpec<TVariables>,
    variables: TVariables,
    middlewareStates: MiddlewareStates,
    additionalParams: RequestAdditionalParams,
  ) {
    const requestorWithDevTools: NetworkRequestor<TResponse> = (uri, verb, headers, payload) => {
      const request = this.networkRequestor(uri, verb, headers, payload);
      if (!window.__OVERREACT_DEVTOOLS__) {
        return request;
      }
      const { onRequest, onError } = window.__OVERREACT_DEVTOOLS__;
      return request.then(value => {
        onRequest({
          id,
          uri,
          verb,
          headers,
          payload,
          spec: spec.toString(),
          componentName: additionalParams.componentName,
          responseValue: value,
        });
        return value;
      }).catch((exception: unknown) => {
        onError({
          id,
          uri,
          verb,
          headers,
          payload,
          spec: spec.toString(),
          componentName: additionalParams.componentName,
          exception,
        });
        throw exception;
      });
    };

    return (
      uri: string,
      verb: RequestVerb,
      headers?: RequestHeaders,
      payload?: unknown,
    ): RequestExecution<TResponse> => ({
      execute: (sink: RequestSink<TResponse>) => {
        if (Object.keys(this.middlewares).length === 0) {
          requestorWithDevTools(uri, verb, headers, payload)
            .then(value => sink.onComplete(value))
            .catch(error => sink.onError(error));
          return;
        }
        const wrappedRequestor = new WrappedRequestor({
          requestor: requestorWithDevTools,
          uri,
          verb,
          headers,
          payload,
          spec,
          variables,
          store: this.store,
          dataRefId: id,
          middlewareStates,
        });
        requestWithMiddleware(wrappedRequestor, this.middlewares)
          .then(value => sink.onComplete(value))
          .catch(error => sink.onError(error));
      },
    });
  }

  getSchema() {
    return this.schema;
  }

  notifyObservers() {
    this.dataFetcherSubscriber.forEach(subscriber => subscriber.notify(this.requestQueue));
  }

  pushRequest(request: OverreactRequestShape) {
    this.requestQueue.push(request);
    this.notifyObservers();
  }

  removeRequest() {
    return this.requestQueue.shift();
  }

  requestCount() {
    return this.requestQueue.length;
  }

  subscribe(notify: RequestSubscriber['notify']) {
    this.dataFetcherSubscriber.push({ notify });
    return this.dataFetcherSubscriber.length - 1;
  }

  unsubscribe(id: number) {
    return this.dataFetcherSubscriber.splice(id, 1).length;
  }
}
